import { useMemo } from "react";
import { Button, Menu, MenuItem, Theme, useMediaQuery, useTheme } from "@mui/material";
import { usePopover } from "src/hooks/use-popover";
import { ChevronDown as ChevronDownIcon } from "src/icons/chevron-down";
import { Briefcase as BriefcaseIcon } from "src/icons/briefcase";
import useStore from "src/hooks/use-store";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useSetViewContext } from "src/hooks/use-set-view-context";
import { lightActions } from "src/colors";

const MIN_REQUIRED_CONTEXTS = 2;

const checkIsPanelContextSelectorDisabled = (
  siteContext: boolean,
  tenantContext: boolean,
  securityContext: boolean,
) => {
  const available = [siteContext, tenantContext, securityContext].filter(Boolean);
  return available.length < MIN_REQUIRED_CONTEXTS;
};

const PanelContextSelectorComponent = () => {
  const { t } = useTranslation();
  const { user } = useStore();
  const navigate = useNavigate();
  const {
    palette: { mode },
  } = useTheme();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const [anchorRef, open, handleOpen, handleClose] = usePopover();

  const { siteContext, tenantContext, securityContext } = useSetViewContext();

  const currentViewContext = useMemo(() => {
    switch (user.viewContext) {
      case "site":
        return t("common:viewContextSelector.site");

      case "tenant":
        return t("common:viewContextSelector.tenant");

      case "security":
        return t("common:viewContextSelector.security");

      default:
        return t("common:viewContextSelector.placeholder");
    }
  }, [user.viewContext, t]);

  const isPanelContextSelectorDisabled = checkIsPanelContextSelectorDisabled(
    siteContext,
    tenantContext,
    securityContext,
  );

  return (
    <>
      <Button
        color="primary"
        disabled={isPanelContextSelectorDisabled}
        onClick={handleOpen}
        ref={anchorRef}
        size={isMobile ? "medium" : "large"}
        variant="text"
        startIcon={
          <BriefcaseIcon
            fontSize="small"
            sx={{
              color: isPanelContextSelectorDisabled
                ? lightActions.disabled
                : mode === "dark"
                  ? "#fff"
                  : "#000",
              width: "16px",
              display: {
                xs: "none",
                sm: "block",
              },
            }}
          />
        }
        endIcon={<ChevronDownIcon fontSize="small" />}
        sx={{
          ml: 1,
          mr: 2,
          alignItems: "center",
          fontWeight: 400,
          fontSize: {
            xs: "12px",
            sm: "16px",
          },
        }}>
        {currentViewContext}
      </Button>
      <Menu
        sx={{
          minWidth: "242px",
        }}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}>
        <MenuItem
          disabled={!tenantContext}
          onClick={() => {
            navigate("/statistics");

            user.setViewContext("tenant");

            handleClose();
          }}>
          {t("common:viewContextSelector.tenant")}
        </MenuItem>
        <MenuItem
          disabled={!siteContext}
          onClick={() => {
            navigate("/statistics");

            user.setViewContext("site");

            handleClose();
          }}>
          {t("common:viewContextSelector.site")}
        </MenuItem>
        <MenuItem
          disabled={!securityContext}
          onClick={() => {
            navigate("/reservations");

            user.setViewContext("security");

            handleClose();
          }}>
          {t("common:viewContextSelector.security")}
        </MenuItem>
      </Menu>
    </>
  );
};

export const PanelContextSelector = observer(PanelContextSelectorComponent);
